import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "src/lib/utils";
import { Button } from "src/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "src/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

const ComboSearch = ({
	placeholder = "Select option",
	className,
	notFoundMessage = "No results found.",
	inputClassName,
	searchPlaceholder,
	optionsContainerClassName,
	optionRenderer,
	queryFN,
	queryKey,
	mapFN,
	...props
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [open, setOpen] = React.useState(false);
	const [search, setSearch] = React.useState("");

	const { data } = useQuery({
		queryKey: [`combo-${queryKey}`, search],
		queryFn: () => queryFN({ search }),
	});

	const options = data?.data?.data?.map(mapFN) ?? [];

	if ("name" in props) {
		const { name } = props;
		const value = searchParams.get(name);

		return (
			<Popover open={open} onOpenChange={setOpen} modal>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						className={cn(
							"tw-min-w-[170px] tw-bg-white tw-px-4 tw-gap-3 !tw-py-3 !tw-border-[#008080] !tw-text-sm tw-font-normal tw-text-gray-500 tw-h-auto",
							className,
						)}
					>
						{value ? options.find((option) => option.value === value)?.label : placeholder}
						<ChevronsUpDown className="tw-ml-auto tw-opacity-50 tw-size-4 tw-shrink-0" />
					</Button>
				</PopoverTrigger>
				<PopoverContent align="end" className={cn("tw-p-0 tw-min-w-[300px] tw-w-auto", optionsContainerClassName)}>
					<Command
						filter={(_option, search, keywords) => {
							if (!search) return 0;
							return Number(keywords?.some((keyword) => keyword?.toLowerCase().includes(search.toLowerCase())));
						}}
						className="w-full"
					>
						<CommandInput
							className={inputClassName}
							placeholder={searchPlaceholder || "Search..."}
							value={search}
							onValueChange={setSearch}
						/>
						<CommandList>
							<CommandEmpty>{notFoundMessage}</CommandEmpty>
							<CommandGroup className="overflow-y-auto">
								{options.map((option) => (
									<CommandItem
										keywords={[option.label]}
										key={option.value}
										value={option.value}
										className="tw-justify-between tw-capitalize"
										onSelect={(currentValue) => {
											const newValue = currentValue === value ? "" : currentValue;
											searchParams.set(name, newValue);
											setSearchParams(searchParams);
											setOpen(false);
										}}
									>
										{optionRenderer(option.info)}
										<Check className={cn("tw-size", value === option.value ? "tw-opacity-100" : "tw-opacity-0")} />
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		);
	}

	const { onChange, value } = props;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn(
						"tw-min-w-[170px] tw-bg-white tw-px-4 tw-gap-3 !tw-py-3 !tw-border-[#008080] tw-text-sm tw-font-normal tw-text-gray-500 tw-h-auto",
						className,
					)}
				>
					{value ? options.find((option) => option.value === value)?.label : placeholder}
					<ChevronsUpDown className="tw-ml-auto tw-opacity-50 tw-size-4 tw-shrink-0" />
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className={cn("tw-p-0 tw-min-w-[300px] tw-w-auto", optionsContainerClassName)} removePortal>
				<Command
					filter={(_option, search, keywords) => {
						if (!search) return 0;
						return Number(keywords?.some((keyword) => keyword?.toLowerCase().includes(search.toLowerCase())));
					}}
					className="tw-w-full"
				>
					<CommandInput
						className={inputClassName}
						placeholder={searchPlaceholder || "Search..."}
						value={search}
						onValueChange={setSearch}
					/>
					<CommandList>
						<CommandEmpty>{notFoundMessage}</CommandEmpty>
						<CommandGroup className="tw-overflow-y-auto">
							{options.map((option) => (
								<CommandItem
									keywords={[option.label]}
									key={option.value}
									value={option.value}
									className="tw-justify-between tw-capitalize"
									onSelect={(currentValue) => {
										const newValue = currentValue === value ? "" : currentValue;
										onChange?.(newValue, option.info);
										setOpen(false);
									}}
								>
									{optionRenderer(option.info)}
									<Check className={cn("tw-size", value === option.value ? "tw-opacity-100" : "tw-opacity-0")} />
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};

export default ComboSearch;
